/**
 * AI Stream Client — consumes SSE from the agent chat endpoint
 */

import { useAIChatStore } from '@/stores/useAIChatStore';
import { useFileTreeStore } from '@/stores/useFileTreeStore';
import { useIDEStore } from '@/stores/useIDEStore';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

let activeAbortController: AbortController | null = null;

/**
 * Cancel the currently active SSE stream, if any.
 */
export function cancelAgentStream(): void {
  if (activeAbortController) {
    activeAbortController.abort();
    activeAbortController = null;
  }
}

export async function sendAgentMessage(
  projectId: string,
  message: string,
  conversationId?: string | null
): Promise<void> {
  // Cancel any existing stream
  cancelAgentStream();

  const store = useAIChatStore.getState();
  store.addUserMessage(message);
  store.setStreaming(true);

  const token = localStorage.getItem('access_token');
  const abortController = new AbortController();
  activeAbortController = abortController;

  try {
    const response = await fetch(`${API_URL}/agent/${projectId}/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ message, conversationId }),
      signal: abortController.signal,
    });

    if (!response.ok) {
      throw new Error(`Agent error: ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) throw new Error('No response body');

    const decoder = new TextDecoder();
    let buffer = '';
    let assistantText = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      let eventType = '';
      let eventData = '';
      for (const line of lines) {
        if (line.startsWith('event: ')) {
          eventType = line.slice(7).trim();
        } else if (line.startsWith('data: ')) {
          eventData = line.slice(6);
        } else if (line === '' && eventType && eventData) {
          // Empty line signals end of SSE event — process it
          try {
            const data = JSON.parse(eventData);
            handleEvent(eventType, data, projectId);

            if (eventType === 'text') {
              assistantText += data.content || '';
            }

            if (eventType === 'text_delta') {
              assistantText += data.content || '';
            }

            if (eventType === 'done' || eventType === 'complete') {
              const state = useAIChatStore.getState();
              state.finishAssistantMessage(data.response || assistantText);
              if (data.conversationId) {
                state.setConversationId(data.conversationId);
              }
            }
          } catch {
            // Skip malformed JSON
          }
          eventType = '';
          eventData = '';
        }

        // Also handle inline event+data pairs (event: line immediately followed by data: line with no blank separator)
        if (eventType && eventData) {
          try {
            const data = JSON.parse(eventData);
            handleEvent(eventType, data, projectId);

            if (eventType === 'text') {
              assistantText += data.content || '';
            }

            if (eventType === 'text_delta') {
              assistantText += data.content || '';
            }

            if (eventType === 'done' || eventType === 'complete') {
              const state = useAIChatStore.getState();
              state.finishAssistantMessage(data.response || assistantText);
              if (data.conversationId) {
                state.setConversationId(data.conversationId);
              }
            }
          } catch {
            // Skip malformed JSON
          }
          eventType = '';
          eventData = '';
        }
      }
    }

    // Ensure streaming is reset even if no 'done' event was received
    const finalState = useAIChatStore.getState();
    if (finalState.isStreaming) {
      finalState.setStreaming(false);
    }
  } catch (error) {
    // Don't treat abort as an error
    if (error instanceof DOMException && error.name === 'AbortError') {
      useAIChatStore.getState().setStreaming(false);
      return;
    }
    useAIChatStore.getState().setError(
      error instanceof Error ? error.message : 'Stream failed'
    );
  } finally {
    if (activeAbortController === abortController) {
      activeAbortController = null;
    }
  }
}

function handleEvent(type: string, data: any, projectId: string) {
  const chatStore = useAIChatStore.getState();
  const fileTreeStore = useFileTreeStore.getState();

  switch (type) {
    case 'thinking':
      chatStore.addStep({ type: 'thinking', data });
      break;

    case 'text_delta':
      chatStore.appendStreamingText(data.content || '');
      chatStore.addStep({ type: 'text_delta', data });
      break;

    case 'tool_call_start':
      chatStore.addStep({ type: 'tool_call_start', data });
      break;

    case 'tool_call_result':
      chatStore.addStep({ type: 'tool_call_result', data });
      break;

    case 'text':
      chatStore.addStep({ type: 'text', data });
      break;

    case 'file_change': {
      chatStore.addStep({ type: 'file_change', data });
      // Refresh file tree
      fileTreeStore.refreshFile(projectId, data.path);
      // Update content in already-open editor tabs
      if (data.path && data.content != null) {
        const ideStore = useIDEStore.getState();
        const openFile = ideStore.openFiles.find((f) => f.path === data.path);
        if (openFile) {
          ideStore.openFile(data.path, data.content, openFile.language);
        }
      }
      break;
    }

    case 'plan':
      chatStore.setPlan({
        title: data.title || 'Plan',
        markdown: data.plan || data.markdown || '',
        completedSteps: [],
      });
      chatStore.addStep({ type: 'plan', data });
      break;

    case 'plan_update':
      if (data.completedStep) {
        chatStore.updatePlanStep(data.completedStep);
      }
      chatStore.addStep({ type: 'plan_update', data });
      break;

    case 'error':
      chatStore.addStep({ type: 'error', data });
      break;
  }
}
